// WebAudio sink for the emulator cores' APU output.
//
// Couch co-op runs several emulator tiles at once and the user asked for
// "mix all" — every tile audible together, but each with its own volume.
// So audio is split in two:
//
//   AudioMixer   — one AudioContext + a unity master GainNode + one
//                  worklet module load. Owns resume(). One per page.
//   AudioChannel — one AudioWorkletNode + its own per-tile GainNode per
//                  tile, summed into the master. Each tile's host pushes
//                  its frames to its own channel; the WebAudio graph sums
//                  them for free, and each channel's gain is that tile's
//                  volume slider.
//
// The worklet (see audio-worklet.js) maintains a ring buffer per node
// and pulls frames at the device clock. When a writer falls behind
// (script stall, GC pause) the worklet fades to silence over 20ms
// rather than splicing — that splice is the pop source the old
// per-frame BufferSource design produced. It fades back up symmetrically
// when samples resume.
//
// Browsers gate audio behind a user gesture (autoplay policy). Merely
// CONSTRUCTING an AudioContext outside a gesture makes the browser log
// "An AudioContext was prevented from starting automatically" — so we
// don't build it at page load. Crucially the primary tile is created at
// boot (addTile(0,0)) before any click, so createChannel() must NOT force
// the context into existence either: a channel created before the context
// exists stays dormant and is attached to the graph later, when the
// gesture-wired resume() (or a ROM-load getSampleRate(), itself always
// inside a gesture) first builds the context. Born inside a gesture, the
// warning never fires. push() is a no-op while suspended or before the
// worklet has loaded — audio drops silently rather than queueing unbounded.

import workletUrl from "./audio-worklet.js?url";

const SAMPLE_RATE = 48000;

// One per page: the shared context, a unity master gain, and the
// one-time worklet module load. Hands out a channel per tile; per-tile
// volume lives on the channel, not here.
export class AudioMixer {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private workletReady: Promise<void> | null = null;
  // Channels handed out before the context exists; attached in ensureCtx().
  private pending: AudioChannel[] = [];

  constructor() {
    // Fail fast if the platform has no WebAudio at all, but DON'T build
    // the context yet — see ensureCtx() and the file header on why
    // construction is deferred to the first user gesture.
    if (!window.AudioContext) throw new Error("AudioMixer: AudioContext unavailable");
  }

  // Build the context + master gain + worklet load on first use. Called
  // from resume() (inside a gesture, the common path) and from the
  // sync accessors below if they run first. Idempotent.
  private ensureCtx(): AudioContext {
    if (this.ctx) return this.ctx;
    // Some browsers honor sampleRate=48000 on construction; others
    // ignore it and use the system rate. The worklet runs at whatever
    // rate the context settles on, so a mismatch is transparent.
    const C = window.AudioContext;
    const ctx = new C({ sampleRate: SAMPLE_RATE });
    this.ctx = ctx;
    const master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
    this.master = master;
    this.workletReady = this.initWorklet(ctx);
    // Wire up any channels handed out before the context existed (the
    // primary tile is created at boot, before any gesture).
    const pending = this.pending;
    this.pending = [];
    for (const ch of pending) ch.attach(ctx, master, this.workletReady);
    return ctx;
  }

  private async initWorklet(ctx: AudioContext): Promise<void> {
    try {
      await ctx.audioWorklet.addModule(workletUrl);
    } catch (e) {
      console.error("AudioMixer: failed to load audio worklet:", e);
    }
  }

  // Call from a user-gesture handler to unblock playback. Constructs the
  // context (inside the gesture, so the autoplay warning never fires) and
  // waits for the worklet module to finish loading so the first push()
  // after this resolves is guaranteed to land.
  async resume(): Promise<void> {
    const ctx = this.ensureCtx();
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    await this.workletReady;
  }

  // Make a channel for one tile at the given initial volume (0..1). Does
  // NOT build the context (this runs at boot, before any gesture) — the
  // channel stays dormant until the context first exists, then its gain →
  // panner → master chain and worklet node are attached. push() is a
  // silent no-op until then.
  createChannel(initialVolume: number): AudioChannel {
    const ch = new AudioChannel(this, initialVolume);
    if (this.ctx) ch.attach(this.ctx, this.master!, this.workletReady!);
    else this.pending.push(ch);
    return ch;
  }

  // Whether the context is actively running (resumed past the autoplay
  // gate). Channels consult this so a stopped/suspended context drops
  // pushes instead of buffering. False before the first resume() builds
  // the context.
  isRunning(): boolean {
    return this.ctx?.state === "running";
  }

  // The rate the context ACTUALLY settled on (may differ from the
  // requested SAMPLE_RATE — many browsers refuse 48 kHz and use the
  // system rate). The worklet consumes one ring frame per output sample
  // with no resampling, so cores must synthesize at THIS rate or audio
  // overruns the ring and plays at the wrong pitch. Builds the context if
  // it doesn't exist yet so the reported rate is the real settled one.
  getSampleRate(): number {
    return this.ensureCtx().sampleRate;
  }
}

// One per tile. Owns a worklet node, a per-tile volume gain, and a
// stereo panner that places the tile in the field by its grid column.
// Chain: worklet → gain → panner → master.
//
// Created dormant (no context, no nodes) because tiles exist before the
// autoplay gesture. attach() builds the node graph once the mixer's
// context is live; until then volume/pan are held on plain fields and
// push() no-ops. See the AudioMixer header on the gesture deferral.
export class AudioChannel {
  private mixer: AudioMixer;
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private panner: StereoPannerNode | null = null;
  private node: AudioWorkletNode | null = null;
  private disposed = false;
  // Held here until attach() builds the nodes, then mirrored onto them.
  private volume: number;
  private pan = 0;

  constructor(mixer: AudioMixer, initialVolume: number) {
    this.mixer = mixer;
    this.volume = Math.max(0, Math.min(1, initialVolume));
  }

  // Called by the mixer once the shared context exists (at boot the tile
  // predates it). Builds this tile's gain → panner → master chain and,
  // when the worklet module resolves, its worklet node. Idempotent-safe:
  // the mixer only calls this once per channel.
  attach(ctx: AudioContext, master: GainNode, ready: Promise<void>): void {
    if (this.disposed) return;
    this.ctx = ctx;
    // This tile's own volume gain, feeding its panner.
    this.gain = ctx.createGain();
    this.gain.gain.value = this.volume;
    // Stereo placement by grid column. StereoPannerNode applies an
    // equal-power pan law internally, so pan=0 is a true −3dB phantom
    // centre (0.707 to each side) — equal perceived loudness with a
    // hard-panned tile, not the −6dB hole a naive 50/50 split gives.
    this.panner = ctx.createStereoPanner();
    this.panner.pan.value = this.pan;
    this.gain.connect(this.panner);
    this.panner.connect(master);
    void ready.then(() => {
      // The tile may have been removed while the module was loading.
      if (this.disposed) return;
      this.node = new AudioWorkletNode(ctx, "nes-audio", {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
      this.node.connect(this.gain!);
    });
  }

  // Feed one frame's interleaved stereo samples (L0, R0, L1, R1, ...).
  // Transfers ownership of the underlying ArrayBuffer to the worklet
  // thread — the caller (munos-emulator-host) allocates a fresh
  // Float32Array each frame, so it's never re-read after push().
  push(samples: Float32Array): void {
    if (this.disposed) return;
    if (!this.ctx || this.ctx.state !== "running") return;
    if (samples.length === 0) return;
    if (!this.node) return;
    this.node.port.postMessage(samples, [samples.buffer]);
  }

  // Place this tile in the stereo field. pan is −1 (hard left) … 0
  // (phantom centre) … +1 (hard right); the tile manager feeds it a
  // binary −1/0/+1 from the tile's grid column. Equal-power law lives in
  // the node, so callers just pass position, not gains.
  setPan(pan: number): void {
    this.pan = Math.max(-1, Math.min(1, pan));
    if (this.panner) this.panner.pan.value = this.pan;
  }

  // The context's actual sample rate (see AudioMixer.getSampleRate). The
  // tile's host reads this to drive its core's synthesis rate — always at
  // ROM load, i.e. inside a gesture — so we let it build the context (via
  // the mixer) to report the real settled rate rather than a guess.
  getSampleRate(): number {
    return this.mixer.getSampleRate();
  }

  // This tile's volume (0..1), applied immediately to its gain.
  getVolume(): number {
    return this.volume;
  }
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.gain) this.gain.gain.value = this.volume;
  }

  // Tear down when the tile is closed: disconnect from the graph so the
  // nodes are collected and stop summing silence into the master. Safe
  // before attach() — the nodes are simply null.
  dispose(): void {
    this.disposed = true;
    if (this.node) {
      try { this.node.disconnect(); } catch { /* already gone */ }
      this.node = null;
    }
    if (this.gain) { try { this.gain.disconnect(); } catch { /* already gone */ } }
    if (this.panner) { try { this.panner.disconnect(); } catch { /* already gone */ } }
  }
}
